import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Modal,
  Table,
  Text,
  Alert,
  CopyButton,
  ActionIcon,
  Tooltip,
  Stack,
  Group,
} from "@mantine/core";
import {
  getUserRequests, // Now using enhanced version
  getFlightHistory, // New function for actual flight history
} from "../misc/api";
import {
  IconArrowRight,
  IconCheck,
  IconCopy,
  IconInfoCircle,
  IconPlane,
  IconCalendar,
} from "@tabler/icons-react";

export const FlightHistory = () => {
  const [flights, setFlights] = useState([]);
  const [actualFlights, setActualFlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFlightData();
  }, []);

  const loadFlightData = async () => {
    try {
      setLoading(true);

      // Get both flight requests and actual flight history
      const [requestsRes, historyRes] = await Promise.all([
        getUserRequests(),
        getFlightHistory().catch(() => []), // Fallback if flight history not available
      ]);

      setFlights(requestsRes.data || []);
      setActualFlights(historyRes || []);
    } catch (error) {
      console.error("Error loading flight data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPPRStatusColor = (pprInfo) => {
    if (!pprInfo) return "gray";

    switch (pprInfo.status) {
      case "active":
        return pprInfo.can_use ? "green" : "orange";
      case "used":
        return "blue";
      case "expired":
        return "gray";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const renderFlightRequests = () => (
    <Table striped highlightOnHover>
      <thead>
        <tr>
          <th>PPR Number</th>
          <th>Aircraft</th>
          <th>Route</th>
          <th>Date</th>
          <th>Type</th>
          <th>Status</th>
          <th>Payment</th>
        </tr>
      </thead>
      <tbody style={{ textAlign: "center" }}>
        {flights?.map((flight) => (
          <tr key={flight.request_id}>
            <td>
              {flight.ppr_number ? (
                <Group spacing="xs">
                  <Text family="monospace" weight={500}>
                    {flight.ppr_number}
                  </Text>
                  <CopyButton value={flight.ppr_number}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied" : "Copy PPR"}>
                        <ActionIcon
                          size="sm"
                          onClick={copy}
                          color={copied ? "teal" : "gray"}
                        >
                          {copied ? (
                            <IconCheck size={12} />
                          ) : (
                            <IconCopy size={12} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              ) : (
                <Text color="dimmed">No PPR</Text>
              )}
            </td>
            <td>
              <div>
                <Text size="sm" weight={500}>
                  {flight.plane_number}
                </Text>
                <Text size="xs" color="dimmed">
                  {flight.plane_type}
                </Text>
              </div>
            </td>
            <td>
              <Text size="sm">
                {flight.departure_place} → {flight.arrival_place}
              </Text>
            </td>
            <td>
              <Text size="sm">
                {flight.dof ? new Date(flight.dof).toLocaleDateString() : "N/A"}
              </Text>
            </td>
            <td>
              <Badge variant="outline">
                {flight.ppr_info?.flight_type || flight.flight_type || "N/A"}
              </Badge>
            </td>
            <td>
              <Badge color={getPPRStatusColor(flight.ppr_info)}>
                {flight.ppr_info?.status || flight.status}
              </Badge>
            </td>
            <td>
              {flight.ppr_info?.payment_status === "paid" ? (
                <Badge color="green">Paid</Badge>
              ) : flight.ppr_info?.needs_payment ? (
                <Badge color="orange">Pending</Badge>
              ) : (
                <Badge color="gray">N/A</Badge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderActualFlights = () => (
    <Table striped highlightOnHover>
      <thead style={{ textAlign: "center" }}>
        <tr>
          <th>Date</th>
          <th>Aircraft</th>
          <th>Route</th>
          <th>PPR</th>
          <th>Times</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody style={{ textAlign: "center" }}>
        {actualFlights.map((flight) => (
          <tr key={flight.fid}>
            <td>
              <Text size="sm">
                {new Date(flight.dof || flight.created).toLocaleDateString()}
              </Text>
            </td>
            <td>
              <div>
                <Text size="sm" weight={500}>
                  {flight.plane_nr}
                </Text>
                <Text size="xs" color="dimmed">
                  {flight.plane_type}
                </Text>
              </div>
            </td>
            <td>
              <Text size="sm">
                {flight.departure_place} → {flight.arrival_place}
              </Text>
            </td>
            <td>
              <Text family="monospace" size="sm">
                {flight.ppr_number || flight.ppr}
              </Text>
            </td>
            <td>
              <div>
                {flight.departure_time && (
                  <Text size="xs">Dep: {flight.departure_time}</Text>
                )}
                {flight.arrival_time && (
                  <Text size="xs">Arr: {flight.arrival_time}</Text>
                )}
              </div>
            </td>
            <td>
              <Badge
                color={
                  flight.ppr_validation_status === "valid" ? "green" : "orange"
                }
              >
                {flight.ppr_validation_status || "Unknown"}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <Stack align="center" spacing="md" style={{ padding: 40 }}>
        <IconPlane size={48} />
        <Text>Loading flight history...</Text>
      </Stack>
    );
  }

  return (
    <Stack spacing="xl" style={{ padding: 20 }}>
      <Group position="apart">
        <Text size="xl" weight={700}>
          Flight History
        </Text>
        <Button
          leftIcon={<IconPlane />}
          onClick={() => (window.location.href = "/request")}
        >
          Request New Flight
        </Button>
      </Group>

      {/* Flight Requests */}
      <div>
        <Group spacing="xs" mb="md">
          <IconCalendar size={20} />
          <Text size="lg" weight={500}>
            Flight Requests
          </Text>
          <Badge>{flights.length}</Badge>
        </Group>

        {flights.length === 0 ? (
          <Alert icon={<IconInfoCircle />}>
            No flight requests found.{" "}
            <Button
              variant="subtle"
              size="xs"
              onClick={() => (window.location.href = "/request")}
            >
              Request your first flight
            </Button>
          </Alert>
        ) : (
          renderFlightRequests()
        )}
      </div>

      {/* Actual Flight History */}
      {actualFlights.length > 0 && (
        <div>
          <Group spacing="xs" mb="md">
            <IconPlane size={20} />
            <Text size="lg" weight={500}>
              Completed Flights
            </Text>
            <Badge>{actualFlights.length}</Badge>
          </Group>

          {renderActualFlights()}
        </div>
      )}
    </Stack>
  );
};
